// src/pages/analytics/SalesByRepTab.tsx
//
// "Sales by Rep" dashboard — replicates Devash's monthly per-rep scorecard
// Excel. Two stacked sections per selected sales rep:
//
//   1. KPI table: one row per customer assigned to the rep, with last-week
//      sales, N-week averages, weekly returns, units, cases (CEIL of
//      Quantity / Invent.Units2 per line), AR aging, discount, and the
//      last-invoice unit price for the configured per-tenant SKUs.
//
//   2. Net Case trend: one row per customer, showing 52wk + 12wk average
//      net cases plus the last N weeks (default 10) as columns.
//
// Filters above both tables: Area + Cust Type dropdowns. KPI table headers
// are click-to-sort. Filtering and sort are client-side on loaded data.

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Users, ArrowUp, ArrowDown } from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import { useAuthContext } from '../../hooks/useAuth';
import type { SalesByRepKpiRow, SalesByRepTrendRow } from '../../types';
import { subcategoryOptions } from './itemSubcategories';

// Per-tenant SKU columns surfaced on the right side of the KPI table. Each
// tenant has its own Advantage DB and its own item codes; this map just
// declares which SKUs (if any) get a pricing column for a given slug. A
// tenant with no entry sees no pricing columns — the rest of the report
// (sales/returns/AR/discount) works for everyone universally.
const TENANT_PRICE_ITEMS: Record<string, { label: string; itemNo: string }[]> = {
  devash: [
    { label: 'HG HVD', itemNo: '1011' },
    { label: 'QT HVD', itemNo: '1012' },
    { label: '8oz HVD', itemNo: '1013' },
  ],
};

const fmtCur = (n: number, decimals = 0) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  }).format(n);

const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(Math.round(n));
const fmtPct = (n: number) => `${Math.round(n)}%`;

// Sort key options for the KPI table — string keys matching SalesByRepKpiRow
// columns. 'name' / 'area' / 'custType' sort as strings; rest numeric.
type SortKey =
  | 'code' | 'name' | 'area' | 'custType'
  | 'lastWeekSales' | 'avgWeeklySales' | 'avgWeeklyReturns'
  | 'avgWeeklyUnitsSold' | 'avgWeeklyUnitsReturned'
  | 'avgWeeklyCasesSold' | 'avgWeeklyCasesReturned'
  | 'arOutstanding' | 'arDays' | 'discount';

type SortDir = 'asc' | 'desc';

export function SalesByRepTab() {
  const { activeTenant, claims } = useAuthContext();
  const tenantSlug = activeTenant?.slug ?? claims?.tenant_slug ?? '';
  const priceItemsConfig = TENANT_PRICE_ITEMS[tenantSlug] ?? [];
  const priceItemsCsv = priceItemsConfig.map((p) => p.itemNo).join(',');

  // Rep dropdown
  const repsQ = useQuery({
    queryKey: ['sales-by-rep', 'reps'],
    queryFn: () => api.getSalesReps(),
    retry: 1, staleTime: 5 * 60_000,
  });
  const reps = repsQ.data?.reps ?? [];
  const [rep, setRep] = useState<string>('');
  useMemo(() => { if (reps.length > 0 && !rep) setRep(reps[0]); }, [reps, rep]);

  // Item filters narrow sales / returns / cases server-side (agent 3.22.0+);
  // the customer list and A/R columns stay whole.
  const [itemCategory, setItemCategory] = useState('');
  const [itemSubcategory, setItemSubcategory] = useState('');
  const { data: filters } = useQuery({
    queryKey: ['v2-analyticsFilters'],
    queryFn: () => api.getAnalyticsFilters(),
    staleTime: 5 * 60 * 1000,
  });
  const itemCategories = filters?.itemCategories ?? [];
  const subcats = subcategoryOptions(filters, itemCategory);

  const kpisQ = useQuery({
    queryKey: ['sales-by-rep', 'kpis', rep, priceItemsCsv, itemCategory, itemSubcategory],
    queryFn: () => api.getSalesByRepKpis({ rep, weeks: 12, priceItems: priceItemsCsv || undefined, itemCategory, itemSubcategory }),
    enabled: !!rep, retry: 1, staleTime: 60_000,
  });

  const trendQ = useQuery({
    queryKey: ['sales-by-rep', 'trend', rep, itemCategory, itemSubcategory],
    queryFn: () => api.getSalesByRepTrend({ rep, weeks: 10, itemCategory, itemSubcategory }),
    enabled: !!rep, retry: 1, staleTime: 60_000,
  });

  // Filters (lifted so both tables stay consistent)
  const [areaFilter, setAreaFilter]         = useState<string>('');
  const [custTypeFilter, setCustTypeFilter] = useState<string>('');

  const allAreas = useMemo(
    () => Array.from(new Set((kpisQ.data?.rows ?? []).map((r) => r.area).filter(Boolean))).sort(),
    [kpisQ.data]
  );
  const allCustTypes = useMemo(
    () => Array.from(new Set((kpisQ.data?.rows ?? []).map((r) => r.custType).filter(Boolean))).sort(),
    [kpisQ.data]
  );

  // Apply filters, then expose filtered kpi rows + matching trend rows
  const filteredKpiRows = useMemo(() => {
    let rows = kpisQ.data?.rows ?? [];
    if (areaFilter)     rows = rows.filter((r) => r.area === areaFilter);
    if (custTypeFilter) rows = rows.filter((r) => r.custType === custTypeFilter);
    return rows;
  }, [kpisQ.data, areaFilter, custTypeFilter]);

  const allowedCodes = useMemo(
    () => new Set(filteredKpiRows.map((r) => r.code)),
    [filteredKpiRows]
  );

  const filteredTrendRows = useMemo(
    () => (trendQ.data?.rows ?? []).filter((r) => allowedCodes.has(r.code)),
    [trendQ.data, allowedCodes]
  );

  const isLoading  = kpisQ.isLoading || trendQ.isLoading;
  const isFetching = kpisQ.isFetching || trendQ.isFetching;

  function refetchAll() { kpisQ.refetch(); trendQ.refetch(); }

  return (
    <div className="space-y-6">
      {/* Header + rep picker + filters */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-slate-500" />
          <h2 className="text-base font-semibold text-white">Sales by Rep</h2>
        </div>

        <FilterField label="Sales Rep">
          <select
            value={rep}
            onChange={(e) => setRep(e.target.value)}
            className="px-3 py-1.5 rounded-md bg-slate-900/60 border border-slate-700/50 text-sm text-white focus:outline-none focus:border-blue-500/50"
            disabled={repsQ.isLoading || reps.length === 0}
          >
            {repsQ.isLoading && <option>Loading…</option>}
            {!repsQ.isLoading && reps.length === 0 && <option>No reps found</option>}
            {reps.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </FilterField>

        <FilterField label="Area">
          <select
            value={areaFilter}
            onChange={(e) => setAreaFilter(e.target.value)}
            className="px-3 py-1.5 rounded-md bg-slate-900/60 border border-slate-700/50 text-sm text-white focus:outline-none focus:border-blue-500/50 min-w-[110px]"
            disabled={allAreas.length === 0}
          >
            <option value="">All Areas</option>
            {allAreas.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </FilterField>

        <FilterField label="Cust Type">
          <select
            value={custTypeFilter}
            onChange={(e) => setCustTypeFilter(e.target.value)}
            className="px-3 py-1.5 rounded-md bg-slate-900/60 border border-slate-700/50 text-sm text-white focus:outline-none focus:border-blue-500/50 min-w-[110px]"
            disabled={allCustTypes.length === 0}
          >
            <option value="">All Types</option>
            {allCustTypes.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </FilterField>

        {itemCategories.length > 0 && (
          <FilterField label="Item Category">
            <select
              value={itemCategory}
              onChange={(e) => { setItemCategory(e.target.value); setItemSubcategory(''); }}
              className="px-3 py-1.5 rounded-md bg-slate-900/60 border border-slate-700/50 text-sm text-white focus:outline-none focus:border-blue-500/50 min-w-[110px]"
            >
              <option value="">All Categories</option>
              {itemCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </FilterField>
        )}

        {subcats && (
          <FilterField label="Item Subcategory">
            <select
              value={itemSubcategory}
              onChange={(e) => setItemSubcategory(e.target.value)}
              className="px-3 py-1.5 rounded-md bg-slate-900/60 border border-slate-700/50 text-sm text-white focus:outline-none focus:border-blue-500/50 min-w-[110px]"
              disabled={subcats.length === 0}
            >
              <option value="">All Subcategories</option>
              {subcats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </FilterField>
        )}

        <div className="flex-1" />

        <button
          onClick={refetchAll}
          disabled={isLoading || isFetching}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Errors */}
      {(repsQ.error || kpisQ.error || trendQ.error) && (
        <div className="p-3 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-300">
          {(repsQ.error || kpisQ.error || trendQ.error) instanceof Error
            ? (repsQ.error || kpisQ.error || trendQ.error)!.toString()
            : 'Failed to load data. Check the agent connection.'}
        </div>
      )}

      {/* KPI table */}
      {kpisQ.data && (
        <KpiTable
          rows={filteredKpiRows}
          totalCount={kpisQ.data.rows.length}
          weeks={kpisQ.data.weeks}
          priceColumns={priceItemsConfig}
        />
      )}

      {/* Trend table */}
      {trendQ.data && (
        <TrendTable
          rows={filteredTrendRows}
          weekLabels={trendQ.data.weekLabels}
        />
      )}

      {/* Empty state */}
      {!isLoading && filteredKpiRows.length === 0 && (
        <div className="p-8 text-center text-sm text-slate-500 border border-slate-700/30 rounded-xl bg-slate-800/20">
          {kpisQ.data?.rows.length === 0
            ? <>No customers assigned to <span className="text-slate-300">{rep}</span>.</>
            : <>No customers match the current filters.</>}
        </div>
      )}
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <label className="text-xs text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

// ── KPI table ──────────────────────────────────────────────────

function KpiTable({
  rows, totalCount, weeks, priceColumns,
}: {
  rows: SalesByRepKpiRow[];
  totalCount: number;
  weeks: number;
  priceColumns: { label: string; itemNo: string }[];
}) {
  const [sortKey, setSortKey] = useState<SortKey>('avgWeeklySales');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const sortedRows = useMemo(() => {
    const out = [...rows];
    out.sort((a, b) => {
      const av = a[sortKey] as string | number;
      const bv = b[sortKey] as string | number;
      const cmp = typeof av === 'string' && typeof bv === 'string'
        ? av.localeCompare(bv)
        : (Number(av) || 0) - (Number(bv) || 0);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return out;
  }, [rows, sortKey, sortDir]);

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir(k === 'code' || k === 'name' || k === 'area' || k === 'custType' ? 'asc' : 'desc'); }
  }

  // Totals row (sum of money/units/cases; average of arDays + discount)
  const totals = useMemo(() => {
    if (sortedRows.length === 0) return null;
    const sum = (k: keyof SalesByRepKpiRow) =>
      sortedRows.reduce((s, r) => s + ((r[k] as number) || 0), 0);
    const avg = (k: keyof SalesByRepKpiRow) => sum(k) / sortedRows.length;
    return {
      lastWeekSales:          sum('lastWeekSales'),
      avgWeeklySales:         sum('avgWeeklySales'),
      avgWeeklyReturns:       sum('avgWeeklyReturns'),
      avgWeeklyUnitsSold:     sum('avgWeeklyUnitsSold'),
      avgWeeklyUnitsReturned: sum('avgWeeklyUnitsReturned'),
      avgWeeklyCasesSold:     sum('avgWeeklyCasesSold'),
      avgWeeklyCasesReturned: sum('avgWeeklyCasesReturned'),
      arOutstanding:          sum('arOutstanding'),
      arDays:                 avg('arDays'),
      discount:               avg('discount'),
    };
  }, [sortedRows]);

  return (
    <div className="rounded-xl bg-slate-800/20 border border-slate-700/30 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-700/30 flex items-baseline gap-2">
        <h3 className="text-sm font-semibold text-white">Key Comparisons</h3>
        <span className="text-xs text-slate-500">
          {sortedRows.length}{sortedRows.length !== totalCount ? ` of ${totalCount}` : ''} customers · {weeks}-week lookback
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left border-b border-slate-700/30">
              <SortableTh label={'Cust\n#'}            sortKey="code"                   active={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh label="Area"                 sortKey="area"                   active={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh label={'Cust\nType'}         sortKey="custType"               active={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh label="Name"                 sortKey="name"                   active={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh label={'Last Week\nSales'}   sortKey="lastWeekSales"          active={sortKey} dir={sortDir} onClick={toggleSort} right />
              <SortableTh label={`${weeks}-Wk Avg\nWeekly`} sortKey="avgWeeklySales"    active={sortKey} dir={sortDir} onClick={toggleSort} right />
              <SortableTh label={'Avg Weekly\nReturn'} sortKey="avgWeeklyReturns"       active={sortKey} dir={sortDir} onClick={toggleSort} right />
              <SortableTh label={'Avg Units\nSold'}    sortKey="avgWeeklyUnitsSold"     active={sortKey} dir={sortDir} onClick={toggleSort} right />
              <SortableTh label={'Avg Units\nReturned'} sortKey="avgWeeklyUnitsReturned" active={sortKey} dir={sortDir} onClick={toggleSort} right />
              <SortableTh label={'Avg Cases\nSold'}    sortKey="avgWeeklyCasesSold"     active={sortKey} dir={sortDir} onClick={toggleSort} right />
              <SortableTh label={'Avg Cases\nReturned'} sortKey="avgWeeklyCasesReturned" active={sortKey} dir={sortDir} onClick={toggleSort} right />
              <SortableTh label={'AR\nOutstanding'}    sortKey="arOutstanding"          active={sortKey} dir={sortDir} onClick={toggleSort} right />
              <SortableTh label={'AR\nDays'}           sortKey="arDays"                 active={sortKey} dir={sortDir} onClick={toggleSort} right />
              <SortableTh label="Discount"             sortKey="discount"               active={sortKey} dir={sortDir} onClick={toggleSort} right />
              {priceColumns.map((p) => (
                <Th key={p.itemNo} right>{p.label}{'\n'}Price</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => (
              <tr key={r.code} className="border-b border-slate-700/20 hover:bg-slate-800/40">
                <Td>{r.code}</Td>
                <Td>{r.area}</Td>
                <Td>{r.custType}</Td>
                <Td className="font-medium text-slate-200 max-w-[180px] truncate" title={r.name}>{r.name}</Td>
                <Td right>{fmtCur(r.lastWeekSales)}</Td>
                <Td right>{fmtCur(r.avgWeeklySales)}</Td>
                <Td right className={r.avgWeeklyReturns < 0 ? 'text-rose-400' : ''}>{fmtCur(r.avgWeeklyReturns)}</Td>
                <Td right>{fmtNum(r.avgWeeklyUnitsSold)}</Td>
                <Td right className={r.avgWeeklyUnitsReturned > 0 ? 'text-rose-400' : ''}>{fmtNum(r.avgWeeklyUnitsReturned)}</Td>
                <Td right>{fmtNum(r.avgWeeklyCasesSold)}</Td>
                <Td right className={r.avgWeeklyCasesReturned > 0 ? 'text-rose-400' : ''}>{fmtNum(r.avgWeeklyCasesReturned)}</Td>
                <Td right>{fmtCur(r.arOutstanding)}</Td>
                <Td right>{Math.round(r.arDays)}</Td>
                <Td right>{fmtPct(r.discount)}</Td>
                {priceColumns.map((p) => {
                  const px = r.prices?.[p.itemNo];
                  return (
                    <Td key={p.itemNo} right>
                      {px != null && px > 0 ? fmtCur(px, 2) : <span className="text-slate-600">—</span>}
                    </Td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {totals && (
            <tfoot>
              <tr className="bg-slate-900/40 border-t border-slate-700/40 font-semibold text-slate-200">
                <Td colSpan={4}>TOTAL</Td>
                <Td right>{fmtCur(totals.lastWeekSales)}</Td>
                <Td right>{fmtCur(totals.avgWeeklySales)}</Td>
                <Td right className="text-rose-400">{fmtCur(totals.avgWeeklyReturns)}</Td>
                <Td right>{fmtNum(totals.avgWeeklyUnitsSold)}</Td>
                <Td right>{fmtNum(totals.avgWeeklyUnitsReturned)}</Td>
                <Td right>{fmtNum(totals.avgWeeklyCasesSold)}</Td>
                <Td right>{fmtNum(totals.avgWeeklyCasesReturned)}</Td>
                <Td right>{fmtCur(totals.arOutstanding)}</Td>
                <Td right>{Math.round(totals.arDays)}</Td>
                <Td right>{fmtPct(totals.discount)}</Td>
                {priceColumns.map((p) => <Td key={p.itemNo} right />)}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ── Trend table ─────────────────────────────────────────────────

function TrendTable({
  rows, weekLabels,
}: {
  rows: SalesByRepTrendRow[];
  weekLabels: string[];
}) {
  const totals = useMemo(() => {
    if (rows.length === 0) return null;
    return {
      avg52w: rows.reduce((s, r) => s + r.avg52w, 0),
      avg12w: rows.reduce((s, r) => s + r.avg12w, 0),
      weekly: weekLabels.map((_, i) => rows.reduce((s, r) => s + (r.weekly[i] || 0), 0)),
    };
  }, [rows, weekLabels]);

  return (
    <div className="rounded-xl bg-slate-800/20 border border-slate-700/30 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-700/30 flex items-baseline gap-2">
        <h3 className="text-sm font-semibold text-white">Trend — Net Cases per Week</h3>
        <span className="text-xs text-slate-500">{rows.length} customers · {weekLabels.length} weeks shown</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left border-b border-slate-700/30">
              <Th>{'Cust\n#'}</Th>
              <Th>Area</Th>
              <Th>{'Cust\nType'}</Th>
              <Th>Name</Th>
              <Th right>{'52-Wk\nAvg'}</Th>
              <Th right>{'12-Wk\nAvg'}</Th>
              {weekLabels.map((w) => (
                <Th key={w} right>{formatWeekLabel(w)}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code} className="border-b border-slate-700/20 hover:bg-slate-800/40">
                <Td>{r.code}</Td>
                <Td>{r.area}</Td>
                <Td>{r.custType}</Td>
                <Td className="font-medium text-slate-200 max-w-[180px] truncate" title={r.name}>{r.name}</Td>
                <Td right>{fmtNum(r.avg52w)}</Td>
                <Td right>{fmtNum(r.avg12w)}</Td>
                {r.weekly.map((n, i) => (
                  <Td key={i} right className={n < 0 ? 'text-rose-400' : ''}>
                    {fmtNum(n)}
                  </Td>
                ))}
              </tr>
            ))}
          </tbody>
          {totals && (
            <tfoot>
              <tr className="bg-slate-900/40 border-t border-slate-700/40 font-semibold text-slate-200">
                <Td colSpan={4}>TOTAL</Td>
                <Td right>{fmtNum(totals.avg52w)}</Td>
                <Td right>{fmtNum(totals.avg12w)}</Td>
                {totals.weekly.map((n, i) => (
                  <Td key={i} right className={n < 0 ? 'text-rose-400' : ''}>{fmtNum(n)}</Td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function formatWeekLabel(yyyymmdd: string): string {
  const parts = yyyymmdd.split('-');
  if (parts.length !== 3) return yyyymmdd;
  const m = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  if (!m || !d) return yyyymmdd;
  return `${m}/${d}`;
}

// ── Cell helpers ───────────────────────────────────────────────

function Th({ children, right }: { children?: React.ReactNode; right?: boolean }) {
  return (
    <th className={`px-1.5 py-1 font-medium text-slate-400 align-bottom leading-tight whitespace-pre-line ${right ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );
}

function SortableTh({
  label, sortKey, active, dir, onClick, right,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  dir: SortDir;
  onClick: (k: SortKey) => void;
  right?: boolean;
}) {
  const isActive = active === sortKey;
  return (
    <th
      onClick={() => onClick(sortKey)}
      className={`px-1.5 py-1 font-medium align-bottom leading-tight whitespace-pre-line cursor-pointer select-none transition-colors ${
        isActive ? 'text-blue-300' : 'text-slate-400 hover:text-slate-200'
      } ${right ? 'text-right' : 'text-left'}`}
    >
      <span className={`inline-flex items-baseline gap-1 ${right ? 'justify-end w-full' : ''}`}>
        <span>{label}</span>
        {isActive && (dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
      </span>
    </th>
  );
}

function Td({
  children, right, colSpan, className = '', title,
}: {
  children?: React.ReactNode; right?: boolean; colSpan?: number; className?: string; title?: string;
}) {
  return (
    <td
      colSpan={colSpan}
      title={title}
      className={`px-1.5 py-1 tabular-nums whitespace-nowrap text-slate-300 ${right ? 'text-right' : ''} ${className}`}
    >
      {children}
    </td>
  );
}
